import { Box, Flex, HStack, IconButton, Link, Text, Tooltip, useColorMode, VStack, Wrap } from '@chakra-ui/react'
import React from 'react'
import { FiGithub, FiExternalLink } from 'react-icons/fi'
import { motion } from 'framer-motion'
import { getTokens } from '../../config/colorTokens'

const Project = (props) => {
	const { colorMode } = useColorMode()
	const t = getTokens(colorMode)
	const tech = props.tech || []
	const actions = [
		{ label: 'Open project', href: props.website, icon: <FiExternalLink /> },
		{ label: 'Open source', href: props.github, icon: <FiGithub /> }
	].filter((action) => action.href && action.href.length !== 0)

	const renderActions = (display, size = 'sm') => (
		<HStack display={display} spacing={2} flexShrink={0}>
			{actions.map((action) => (
				<Tooltip key={action.label} label={action.label} hasArrow bg={t.tooltipBg} color={t.tooltipColor}>
					<IconButton
						aria-label={action.label}
						as={Link}
						href={action.href}
						target="_blank"
						rel="noreferrer"
						icon={action.icon}
						size={size}
						border="1px"
						borderRadius="8px"
						borderColor={t.borderCard}
						bg="transparent"
						color={t.textSecondary}
						_hover={{
							textDecoration: 'none',
							bg: t.hoverBg,
							color: t.textPrimary,
							transform: 'translateY(-1px)'
						}}
						_active={{
							transform: 'translateY(0)'
						}}
					/>
				</Tooltip>
			))}
		</HStack>
	)

	const renderTags = () => (
		tech.length > 0 && (
			<Wrap spacing={2}>
				{tech.map((skill) => (
					<Box
						key={skill}
						as="span"
						display="inline-flex"
						alignItems="center"
						minH="26px"
						border="1px"
						borderRadius="8px"
						borderColor={t.borderSubtle}
						bg={t.surfaceAlpha}
						color={t.textSecondary}
						fontSize="xs"
						fontWeight="medium"
						lineHeight="1"
						px={2.5}
						transition="all 0.2s ease"
						_hover={{
							bg: t.hoverBg,
							borderColor: t.accent,
							color: t.accent
						}}
					>
						{skill}
					</Box>
				))}
			</Wrap>
		)
	)

	const iconTile = (
		<Flex
			align="center"
			justify="center"
			boxSize={{ base: '38px', md: '42px' }}
			border="1px"
			borderRadius="8px"
			borderColor={t.borderSubtle}
			bg={t.surfaceAlpha}
			color={t.iconDefault}
			fontSize="xl"
			lineHeight="1"
			flexShrink={0}
		>
			{props.emoji}
		</Flex>
	)

	return (
		<motion.div
			variants={props.variants}
			whileHover={{
				scale: 1.02,
				transition: { type: 'spring', stiffness: 300, damping: 20 }
			}}>
				<Box
					border="1px"
					borderRadius="10px"
					borderColor={t.borderCard}
					bg="transparent"
					backdropFilter={'blur(5px)'}
					width="100%"
					p={{ base: 3.5, md: 5 }}>
					<VStack display={{ base: 'flex', md: 'none' }} align="stretch" spacing={3}>
						<Flex align="center" gap={3}>
							{iconTile}
							<Text
								color={t.textPrimary}
								fontSize="md"
								fontWeight="semibold"
								lineHeight="1.25"
								flex="1"
								minW={0}
								wordBreak="break-word"
							>
								{props.name}
							</Text>
							{renderActions('flex', 'xs')}
						</Flex>

						<Text color={t.textMuted} fontSize="sm" lineHeight="1.55">
							{props.description}
						</Text>

						{renderTags()}
					</VStack>

					<Flex display={{ base: 'none', md: 'flex' }} align="start" gap={4}>
						{iconTile}

						<VStack align="stretch" spacing={3} minW={0} flex="1">
							<Flex align="start" justify="space-between" gap={3}>
								<Box minW={0}>
									<Text
										color={t.textPrimary}
										fontSize={{ base: 'md', md: 'lg' }}
										fontWeight="semibold"
										lineHeight="1.25"
										wordBreak="break-word"
									>
										{props.name}
									</Text>
									<Text color={t.textMuted} fontSize="sm" mt={1} lineHeight="1.55">
										{props.description}
									</Text>
								</Box>

								{renderActions('flex')}
							</Flex>

							{renderTags()}
						</VStack>
					</Flex>
				</Box>
			</motion.div>
	)
}

export default Project
